import React, { FC, KeyboardEventHandler, memo, useCallback, useEffect, useState } from 'react'

import { Button } from '@Components/Button'
import { SharedFacetItemProps } from '@Components/Facet/Display/Items/ItemProps'
import { TextField } from '@Components/FormElements/UI/TextField'
import { useTranslation } from '@Globals/Translations'

import styles from './Range.module.scss'

interface RangeItemProps extends SharedFacetItemProps {
  closeHandler?: () => void
  disabled?: boolean
  from: number
  max: number
  min: number
  onChange?: (from: number, to: number) => void
  to: number
}
export const RangeItem: FC<RangeItemProps> = memo(({
  closeHandler,
  disabled = false,
  from,
  id,
  max,
  min,
  onChange,
  to,
}) => {
  const { t } = useTranslation('common')
  const [state, setState] = useState<{ from: number | '', to: number }>(
    {
      from: Math.floor(from) || '',
      to: Math.ceil(to),
    },
  )

  const onChangeFrom = useCallback((event) => {
    const value = event.target.value === '' ? 0 : parseInt(event.target.value, 10)
    if (!Number.isNaN(value)) {
      setState(prev => ({ ...prev, from: value }))
    }
  }, [setState])

  const onChangeTo = useCallback((event) => {
    const value = event.target.value === '' ? 0 : parseInt(event.target.value, 10)
    if (!Number.isNaN(value)) {
      setState(prev => ({ ...prev, to: value }))
    }
  }, [setState])

  const handleOnSubmit = useCallback(() => {
    if (onChange) {
      onChange(state.from || 0, state.to)
    }
    if (closeHandler) {
      closeHandler()
    }
  }, [closeHandler, onChange, state.from, state.to])

  const handleKeyPress = useCallback<KeyboardEventHandler<HTMLInputElement>>((event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleOnSubmit()
    }
  }, [handleOnSubmit])

  // If this facet is reset from outside, we need to return the input fields to their former glory.
  useEffect(() => {
    if (from === 0 && to === max) {
      setState({
        from: '',
        to: Math.ceil(max),
      })
    }
  }, [from, max, to])

  return (
    <div className={styles.rangedInputsRoot} id={id}>
      <TextField
        disabled={disabled}
        id={`${id}-1`}
        label={t('MINIMUM_SHORT_LABEL')}
        labelActive
        max={max}
        min={0}
        onChange={onChangeFrom}
        onKeyPress={handleKeyPress}
        type="text"
        value={state.from}
      />
      <TextField
        disabled={disabled}
        id={`${id}-2`}
        label={t('MAXIMUM_SHORT_LABEL')}
        labelActive
        max={max}
        min={min}
        onChange={onChangeTo}
        onKeyPress={handleKeyPress}
        type="text"
        value={state.to}
      />
      <Button
        color="dark"
        disabled={disabled}
        onClick={handleOnSubmit}
        style={{ padding: '5px 20px' }}
        type="submit"
        variant="outlined"
        width="auto"
      >
        {t('APPLY')}
      </Button>
    </div>
  )
}, (prev, next) => prev.from === next.from && prev.to === next.to && prev.min === next.min && prev.max === next.max && prev.disabled === next.disabled)
